import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { PageTitle } from '@components/PageTitle';
import { queryKeys } from '@constants/queryKeys.constant';
import { ESmartEventTimelineKeyType } from '@enums/smartEvent.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { invalidateSmartEventTimeline } from '@helpers/queryCache/invalidateSmartEventTimeline.helper';
import { updateServiceRequestCounts } from '@helpers/queryCache/updateTabCounts.helper';
import {
  addDocumentsSchema,
  type AddDocumentsDto,
} from '@services/documents/dtos/add.dto';
import { serviceRequestService } from '@services/serviceRequests/service';
import type { GetAllServiceRequestDocumentsOutput } from '@services/serviceRequests/types/getAllDocuments.type';

import { AddDocumentsForm } from '../@components/Documents/AddForm';

export const Route = createFileRoute(
  '/_layout/(private)/service-requests/$id/documents/add',
)({
  component: ServiceRequestDocumentsAdd,
});

function ServiceRequestDocumentsAdd() {
  const navigate = Route.useNavigate();
  const { id: serviceRequestId } = Route.useParams();
  const queryClient = useQueryClient();

  const form = useForm<AddDocumentsDto>({
    resolver: zodResolver(addDocumentsSchema),
    defaultValues: {
      documents: [],
    },
  });

  const { isPending, mutate: addDocument } = useMutation({
    mutationFn: serviceRequestService.addDocuments,
    onError: handleRequestError,
    onSuccess: async ({ uploaded }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.serviceRequests.documents(serviceRequestId),
      });

      queryClient.invalidateQueries({
        queryKey:
          queryKeys.serviceRequests.documentsWithInterventions(
            serviceRequestId,
          ),
      });

      queryClient.setQueriesData<GetAllServiceRequestDocumentsOutput>(
        { queryKey: queryKeys.serviceRequests.documents(serviceRequestId) },
        oldData => {
          if (!oldData) {
            return {
              list: uploaded,
            };
          }

          return {
            list: uploaded.concat(oldData.list),
          };
        },
      );

      updateServiceRequestCounts({
        queryClient,
        serviceRequestId,
        field: 'documents',
        delta: uploaded.length,
      });

      invalidateSmartEventTimeline({
        id: serviceRequestId,
        queryClient,
        type: ESmartEventTimelineKeyType.ServiceRequest,
      });

      toast.success('Success', {
        description: 'Document(s) added successfully',
      });

      navigate({
        to: '/service-requests/$id',
        params: { id: serviceRequestId },
        replace: true,
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    addDocument({ documents: values.documents, id: serviceRequestId });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle
        hasBackButton
        params={{ id: serviceRequestId }}
        title="Add documents"
        to="/service-requests/$id"
      />

      <AddDocumentsForm
        handleSubmit={handleSubmit}
        isLoading={isPending}
        serviceRequestId={serviceRequestId}
      />
    </Form.Provider>
  );
}
